import Header from '../Components/Header';
import Footer from '../Components/Footer';
import DeliverableSection from '../Components/DeliverableSection';
import { Helmet } from 'react-helmet';
import { canonicalUrl } from '../siteConfig';

const DELIVERABLES = [
  {
    title: 'Rack Buildout',
    icon: 'fas fa-server',
    image: 'assets/images/rack.png',
    imageAlt: 'Technicians assembling industrial equipment on a production floor',
    lead: 'Every port labeled, every cable dressed.',
    body: 'Before a single device is mounted, we produce rack elevation drawings that specify equipment placement, power requirements, and cable management. The result is a clean, serviceable rack your team or future contractors can work with confidently.',
    points: [
      'Rack elevation and equipment layout',
      'Patch panel and switch port assignments',
      'Power and UPS planning',
      'Cable dressing and labeling standards',
    ],
  },
  {
    title: 'WiFi Coverage Plans',
    icon: 'fas fa-wifi',
    image: 'assets/images/wifi.png',
    imageAlt: 'Modern office workspace with network planning on display',
    lead: 'Coverage modeled on your actual floor plan.',
    body: 'We map access point locations and expected coverage onto your architectural drawings so there are no surprises after install. Placement accounts for wall materials, ceiling height, user density, and the devices each area will support.',
    points: [
      'AP placement overlaid on floor plans',
      'Coverage zones for office, guest, and specialty areas',
      'Channel and band planning',
      'Mounting and patch connection requirements per AP',
    ],
    reverse: true,
  },
  {
    title: 'Logical Topology',
    icon: 'fas fa-project-diagram',
    image: 'assets/images/logical.png',
    imageAlt: 'Abstract network connectivity visualization with circuit patterns',
    lead: 'A clear map of how everything connects.',
    body: 'The logical topology shows how your network segments relate to each other: firewalls, core switches, VLANs, uplinks, and downstream systems. It is the reference your IT team, integrators, and future vendors will use to understand the environment.',
    points: [
      'VLAN and subnet assignments',
      'Firewall zones and inter-VLAN routing',
      'Switch stacking and uplink design',
      'Third-party system integration points',
    ],
  },
];

const PROFESSIONAL_INSTALL = {
  title: 'Professional Installation',
  icon: 'fas fa-tools',
  image: 'assets/images/unifi-gear.jpg',
  imageAlt: 'Technicians performing on-site equipment installation',
  lead: 'Designed on paper, built in the field.',
  body: 'Once the design is approved, we handle the on-site install: mounting network hardware, patching and terminating at the rack, dressing in-rack cabling, configuring switches and firewalls, deploying access points, and validating everything against the plan. We coordinate with your low-voltage contractors for structured cabling through the building, so drops are in place where the design calls for them.',
  points: [
    'Rack assembly, patching, and termination',
    'Switch, firewall, and access point deployment',
    'Network device configuration and VLAN setup',
    'On-site testing and performance validation',
    'Coordination with low-voltage contractors and project teams',
  ],
  reverse: true,
};

const UNIFI_PRODUCTS = [
  'UniFi Dream Machine gateways',
  'UniFi Wi-Fi 6 & 7 access points',
  'UniFi managed PoE switches',
  'UniFi Protect & Access (optional)',
];

const ONGOING_SUPPORT = {
  title: 'Ongoing Support',
  icon: 'fas fa-headset',
  image: 'assets/images/itsupport.jpg',
  imageAlt: 'Network infrastructure monitoring and connectivity visualization',
  lead: 'Your network does not stop mattering after install day.',
  body: 'We offer ongoing support for the environments we build and maintain. Whether something needs attention or you are planning your next expansion, you have a team that already knows your infrastructure and can act on it quickly.',
  points: [
    'Routine health checks across switches, firewalls, and wireless',
    'Firmware updates and security patch management',
    'Performance review and optimization as usage grows',
    'Remote and on-site troubleshooting when issues come up',
    'Guidance on network changes, additions, and office moves',
  ],
};

export default function NetworkInfrastructure() {
  return (
    <div>
      <Helmet>
        <title>Network Infrastructure Design | Solace Integrations | Westchester, NY</title>
        <link rel="canonical" href={canonicalUrl('/services/network-infrastructure')} />
        <meta
          name="description"
          content="Our network design process covers discovery, rack buildout, wireless coverage planning, logical topology, implementation, and documentation for businesses in Westchester and Fairfield Counties."
        />
      </Helmet>
      <Header isFixed />
      <section className="services">
        <div className="services-hero py-5">
          <div className="container py-md-5 py-2">
            <nav className="net-infra-breadcrumb" aria-label="Breadcrumb">
              <a href="/services">Services</a>
              <span className="net-infra-breadcrumb-sep" aria-hidden="true">/</span>
              <span>Network Infrastructure</span>
            </nav>
            <div className="text-center">
              <h1 className="services-title text-center mb-lg-4 mb-3 px-lg-5">
                Network Infrastructure
              </h1>
              <p className="main_p4 mt-4 mb-0 pt-2 text-center mx-auto text-white">
                A well-designed network starts long before equipment arrives. We follow a structured design
                process from site discovery through as-built documentation, so your infrastructure is
                planned, segmented, and documented before installation begins.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="net-infra-deliverables">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Design Deliverables</h6>
            <h2 className="title-w3l">What You Get Along the Way</h2>
            <p className="net-infra-section-intro mx-auto">
              These are the core design artifacts we produce during a typical network project: visual
              documentation you can review and approve before installation begins.
            </p>
          </div>
          {DELIVERABLES.map((deliverable) => (
            <DeliverableSection key={deliverable.title} {...deliverable} />
          ))}
        </div>
      </section>

      <section className="net-infra-brands">
        <div className="container">
          <div className="text-center mb-4">
            <h6 className="solace-title-subhny mb-2">Networking Platform</h6>
            <h2 className="title-w3l">
              Which Networking Brands Do We Install for Westchester &amp; Fairfield Clients?
            </h2>
            <p className="net-infra-section-intro mx-auto">
              For Westchester and Fairfield County projects, UniFi by Ubiquiti is our primary platform:
              centralized management, no per-device licensing, and hardware that fits homes and small
              offices. We can also extend or integrate with gear you already have when a full replacement
              is not practical.
            </p>
          </div>
          <div className="net-infra-brands-content mx-auto text-center">
            <p className="net-infra-brands-lead">
              On new builds we specify gateways, PoE switches, and Wi-Fi 6/7 access points sized to your
              floor plan. Optional Protect cameras and Access readers run on the same UniFi OS console.
              See the product guide for the full lineup and how each component fits together.
            </p>
            <ul className="net-infra-brands-list">
              {UNIFI_PRODUCTS.map((product) => (
                <li key={product}>{product}</li>
              ))}
            </ul>
            <a href="/products/unifi-networking" className="net-infra-brands-link">
              View the full UniFi product guide
            </a>
          </div>
        </div>
      </section>

      <section className="net-infra-install">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Installation</h6>
            <h2 className="title-w3l">Professional Install</h2>
            <p className="net-infra-section-intro mx-auto">
              Design is only half the job. We install and configure everything on-site so your network
              is production-ready when we leave.
            </p>
          </div>
          <DeliverableSection {...PROFESSIONAL_INSTALL} />
        </div>
      </section>

      <section className="net-infra-support">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">After Install</h6>
            <h2 className="title-w3l">Ongoing Support</h2>
            <p className="net-infra-section-intro mx-auto">
              A network project does not end at handoff. We stay available to keep your infrastructure
              stable, secure, and ready for what comes next.
            </p>
          </div>
          <DeliverableSection {...ONGOING_SUPPORT} />
        </div>
      </section>

      <section className="net-infra-cta">
        <div className="container text-center">
          <h4 className="net-infra-cta-title">Planning a network project?</h4>
          <p className="net-infra-cta-text">
            Tell us about your space and timeline. We will walk you through the design process and what to expect at each stage.
          </p>
          <a href="/contact" className="btn btn-style solace-btn-primary">
            Start a Conversation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
